import { makeStyles } from "@mui/styles";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import { ModuleCarousel } from "./ModuleCarousel";
import React, { useEffect, useRef, useState } from "react";
import { IconButtonOutlined } from "../utils/IconButtonOutlined";
import { ReactComponent as IconChevronLeft } from "../../../assets/IconChevronLeft.svg";
import { ReactComponent as IconChevronRight } from "../../../assets/IconChevronRight.svg";
import { Module } from "../../utils/CourseTree";

const CAROUSEL_MAX_WIDTH = 236;

const useStyles = makeStyles((theme) => ({
  controlsButton: {
    width: 32,
    height: 32,
    position: "absolute",
    boxShadow: "0px 4px 4px 0px #00000040",
  },
  previousButton: {
    left: -16,
    top: "calc(50% - 2px)",
  },
  nextButton: {
    right: -16,
    top: "calc(50% - 2px)",
  },
}));

interface CarouselItemProps {
  item: any[];
  slideCount: number;
  currentSlide: number;
  previousModuleIndex: number | null;
  nextModuleIndex: number | null;
  setCurrentSlide: React.Dispatch<React.SetStateAction<number>>;
}

function CarouselItem(props: CarouselItemProps) {
  const {
    item,
    currentSlide,
    previousModuleIndex,
    nextModuleIndex,
    setCurrentSlide,
    slideCount,
  } = props;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          position: "relative",
          left: -currentSlide * CAROUSEL_MAX_WIDTH,
          transition: "left 0.25s ease-in-out",
          width: `${slideCount * CAROUSEL_MAX_WIDTH}px`,
        }}>
        {item.map((item, index) => {
          return (
            <ModuleCarousel
              moduleId={item.id}
              title={item.name}
              moduleNumber={item.order}
              isModuleLocked={item.is_asset_locked}
              isPreviousModule={index === previousModuleIndex}
              isNextModule={index === nextModuleIndex}
              moduleScore={item.score}
              onFocus={() => setCurrentSlide(index)}
            />
          );
        })}
      </Box>
    </>
  );
}

interface ModuleSectionCarouselProps {
  moduleList: Module[];
  currentModuleId: string;
}

export const ModuleSectionCarousel = (props: ModuleSectionCarouselProps) => {
  const classes = useStyles();
  let { moduleList, currentModuleId } = props;

  // Determine the slide index for the carousel to start at.
  let slideNumber = moduleList.findIndex((item) => item.id === currentModuleId);

  // If the current module is the first in the list, we can't go any earlier,
  // so we default to the first slide, else we show the previous slide
  if (slideNumber !== 0) slideNumber -= 1;

  const [currentSlide, setCurrentSlide] = useState(slideNumber);
  const [showSlides, setShowSlides] = useState<boolean>(false);

  useEffect(() => {
    setCurrentSlide(slideNumber);
  }, [slideNumber]);

  const currentIndex = moduleList.findIndex((item) => item.id === currentModuleId);
  //Remove the current module from the cards as we are already in that module page
  //This ensures only other modules (previous/next) are shown as cards.
  const newModuleList = moduleList.filter((item) => item.id !== currentModuleId);

  const MAX_VISIBLE = 8;
  const SMALL_SIZE = 8;
  const NORMAL_SIZE = 12;

  const total = newModuleList.length;

  const [startIndex, setStartIndex] = useState(0);
  useEffect(() => {
    let nextStart = startIndex;

    // moving right → about to hit right overflow
    if (
      currentSlide === startIndex + MAX_VISIBLE - 1 &&
      startIndex + MAX_VISIBLE < total
    ) {
      nextStart = Math.min(total - MAX_VISIBLE, startIndex + (MAX_VISIBLE - 2));
    }

    // moving left → about to hit left overflow
    else if (currentSlide === startIndex && startIndex > 0) {
      nextStart = Math.max(0, startIndex - (MAX_VISIBLE - 2));
    }

    if (nextStart !== startIndex) {
      setStartIndex(nextStart);
    }
  }, [currentSlide, startIndex, total]);

  const visibleModules = newModuleList.slice(startIndex, startIndex + MAX_VISIBLE);

  //Show module carousel slides if any of the asset is unlocked
  useEffect(() => {
    for (const module of newModuleList) {
      if (module.is_asset_locked === false) {
        setShowSlides(true);
        break;
      }
    }
  }, [newModuleList]);

  let previousModuleIndex = null;
  let nextModuleIndex = null;

  if (currentIndex > 0) {
    previousModuleIndex = currentIndex - 1;
  }
  if (currentIndex < newModuleList.length - 1) {
    nextModuleIndex = currentIndex; // After removal of currentModule, next module shifts to currentIndex
  }

  const handleNext = () => {
    if (!newModuleList.length) return;

    let nextSlide = currentSlide + 1;
    if (nextSlide === newModuleList.length) {
      nextSlide = 0;
    }
    setCurrentSlide(nextSlide);
  };

  const handlePrevious = () => {
    if (!newModuleList.length) return;
    let nextSlide = currentSlide - 1;
    if (nextSlide < 0) {
      nextSlide = newModuleList.length - 1;
    }
    setCurrentSlide(nextSlide);
  };

  const containerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [currentSlide]);

  return (
    <Box
      sx={{
        position: "relative",
        marginTop: "24px",
        width: "100%",
      }}>
      {showSlides ? (
        <>
          <Typography
            variant="h3"
            component={"span"}
            sx={{ fontWeight: "bold", color: "#191919" }}>
            Quick navigation
          </Typography>
          <Box
            ref={containerRef}
            sx={{
              display: "flex",
              flexDirection: "row",
              overflowX: "hidden",
              overflowY: "hidden",
              marginTop: "24px",
              position: "relative",
            }}>
            <CarouselItem
              item={newModuleList}
              currentSlide={currentSlide}
              setCurrentSlide={setCurrentSlide}
              previousModuleIndex={previousModuleIndex}
              nextModuleIndex={nextModuleIndex}
              slideCount={newModuleList.length}
            />
          </Box>
          {currentSlide > 0 && (
            <IconButtonOutlined
              size="medium"
              color="neutral"
              className={`${classes.controlsButton} ${classes.previousButton}`}
              onClick={handlePrevious}
              sx={{ zIndex: "2" }}>
              <IconChevronLeft
                style={{ width: "26px", height: "26px", color: "#0092E0" }}
              />
            </IconButtonOutlined>
          )}
          {newModuleList.length && currentSlide < newModuleList.length - 1 && (
            <IconButtonOutlined
              size="medium"
              color="neutral"
              className={`${classes.controlsButton} ${classes.nextButton}`}
              onClick={handleNext}
              sx={{ zIndex: "2" }}>
              <IconChevronRight
                style={{ width: "26px", height: "26px", color: "#0092E0" }}
              />
            </IconButtonOutlined>
          )}
          {/*Slide notches */}
          <Stack
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "16px",
              width: "360px",
            }}>
            {visibleModules.map((_, index) => {
              const globalIndex = startIndex + index;

              const hiddenBefore = startIndex > 0;
              const hiddenAfter = startIndex + MAX_VISIBLE < total;

              const isLeftOverflow = index === 0 && hiddenBefore;
              const isRightOverflow = index === MAX_VISIBLE - 1 && hiddenAfter;

              const isOverflowIndicator = isLeftOverflow || isRightOverflow;

              return (
                <Box
                  key={globalIndex}
                  tabIndex={0}
                  role="button"
                  aria-label={`Go to slide ${globalIndex + 1} of ${total}`}
                  sx={{
                    borderRadius: "50%",
                    height: isOverflowIndicator ? SMALL_SIZE : NORMAL_SIZE,
                    width: isOverflowIndicator ? SMALL_SIZE : NORMAL_SIZE,
                    border: globalIndex === currentSlide ? "none" : "2px solid #747474",
                    marginX: "4px",
                    backgroundColor: globalIndex === currentSlide ? "#0092E0" : "#FAFAFA",
                    cursor: "pointer",
                  }}
                  onClick={() => setCurrentSlide(globalIndex)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault(); // prevent page scroll on space
                      setCurrentSlide(globalIndex);
                    }
                  }}
                />
              );
            })}
          </Stack>
        </>
      ) : (
        <>
          <Card
            sx={{
              padding: "2rem",
            }}>
            <CardContent
              sx={{
                padding: "0px !important",
              }}>
              <Typography
                sx={{
                  fontSize: "28px",
                  fontWeight: "bold",
                }}
                variant="h3">
                Quick navigation
              </Typography>
              <Typography
                sx={{
                  marginTop: "24px",
                }}
                variant="body1">
                You'll unlock navigation to previous modules as you progress.{" "}
              </Typography>

              {/* Removed per ticket 5405 */}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};
